import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BottleSilhouette from "./BottleSilhouette";
import { api } from "../lib/api";
import { getCategoryColor } from "../lib/constants";

/** Editorial 'Staff Picks Tonight' strip. Prefers admin override (Tonight at BENO), falls back to daily rotation. */
export default function StaffPickStrip() {
  const [pick, setPick] = useState(null);

  useEffect(() => {
    // Prefer admin Tonight override
    api.get("/tonight").then((r) => {
      if (r.data?.active && r.data.gin) {
        setPick({
          bartender: r.data.bartender || "BENO",
          note: r.data.note || `${r.data.bartender || "We"} are pouring ${r.data.gin.name} tonight.`,
          gin: r.data.gin,
          isAdmin: true,
        });
      } else {
        // Fallback to daily staff pick
        api.get("/staff-pick").then((d) => setPick(d.data));
      }
    }).catch(() => {
      api.get("/staff-pick").then((d) => setPick(d.data)).catch(() => {});
    });
  }, []);

  if (!pick || !pick.gin) return null;
  const color = getCategoryColor(pick.gin.category);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="px-5 md:px-12 py-12 max-w-7xl mx-auto"
      data-testid="staff-pick-strip"
    >
      <div className="relative bg-[#101113] border-y border-white/8 p-6 md:p-10 grain overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: `radial-gradient(ellipse at right, ${color}22, transparent 60%)` }} />
        <div className="relative grid grid-cols-12 gap-4 md:gap-10 items-center">
          {/* Stamp area */}
          <div className="col-span-12 md:col-span-3 flex items-center gap-4 md:flex-col md:items-start md:gap-2">
            <div className="border border-[#C17F59] px-3 py-1 inline-block" style={{ borderColor: color }}>
              <p className="overline text-[10px]" style={{ color }}>Staff Pick · Tonight</p>
            </div>
            <p className="font-hand text-3xl md:text-5xl text-[#D89A74] -rotate-2 mt-0 md:mt-2">{pick.bartender}</p>
          </div>

          {/* Bottle */}
          <Link to={`/gin/g/${pick.gin.id}`} className="col-span-3 md:col-span-2 flex items-center justify-center group" data-testid="staff-pick-bottle">
            <div className="relative">
              <div className="absolute inset-0 -m-6 opacity-50 group-hover:opacity-80 transition-opacity"
                   style={{ background: `radial-gradient(circle, ${color}33, transparent 70%)` }} />
              <BottleSilhouette category={pick.gin.category} size={90} />
            </div>
          </Link>

          {/* Copy */}
          <div className="col-span-9 md:col-span-7 space-y-3">
            <p className="font-display text-2xl md:text-3xl leading-snug italic text-white/90">"{pick.note}"</p>
            <div className="flex items-center gap-3 pt-1">
              <Link to={`/gin/g/${pick.gin.id}`} className="font-display text-3xl md:text-4xl hover:text-[#C17F59] transition-colors" data-testid="staff-pick-name">
                {pick.gin.name}
              </Link>
              <span className="overline text-white/40">{pick.gin.country} · ₹{pick.gin.price}</span>
            </div>
            <Link to={`/gin/g/${pick.gin.id}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] hover:text-[#C17F59] transition-colors mt-1"
                  style={{ color }}
                  data-testid="staff-pick-cta">
              Read the bottle
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
